import { put, takeEvery } from "redux-saga/effects";
import api from "../../../utils/apiService";
import { ENDPOINT } from "../../../config/endpoints";
import {
  calculateAndSaveSaleData,
  hasItemLeveErrors,
  resetSaleData,
} from "../../../helpers/saleHelpers";
import { holdSaleData, reopenOrder } from "../../slices/SaleSlice";
import { notifyError } from "../../../effects-bridge";
import { getErrorMessage, pos_errormap } from "../../../utils/posErrors";
import { deleteErrorsField } from "../../../helpers/commonHelpers";
import { orderLevelError } from "../../slices/GlobalSlice";
import { updateAdhocTables } from "../../../helpers/adhocTableHelpers";
import { updateSyncedSale } from "../../../helpers/OfflineHelper";
import { saveBackOrderShowModal } from "../../slices/CustomerSlice";
import {
  ITEM_SOLD_OUT,
  INSUFFICIENT_STOCK,
  METHODS,
} from "../../../data/constants";

export * from "./shared";

/**
 * WEB half of `redux-store/saga/sale`. See `shared.ts` for the eight-axis divergence map.
 *
 * ⚠️ Every core import names the VARIANT, not the folder (hazard 6/16).
 */

/**
 * POST helper. Private to this variant — see `shared.ts` for why it is not shared.
 *
 * ⚠️ Swallows every error and returns `undefined`; `holdSale` then treats a falsy response as
 * failure. Same shape as the other sagas' private `commonPostData`.
 */
async function commonPostData(url: string, data: any) {
  try {
    const postApi = api.request({
      method: "POST",
      url: url,
      data,
      hideLoader: false,
    } as any);
    const fetchResponse = await postApi;
    return fetchResponse;
  } catch (e) {
  }
}

/**
 * Save/hold a sale, then branch on what came back.
 *
 * ⚠️ **`hasItemLeveErrors` always returns `false`** (see its docstring in
 * `helpers/saleHelpers/shared.ts` — the `return true` exits a `.map` callback). So the
 * item-level part of this success guard has never actually excluded anything. Preserved: fixing
 * it belongs to that helper's own ticket, not to this uplift.
 *
 * ⚠️ `updateAdhocTables` runs on EVERY response, before the branch — including error responses.
 */
function* holdSale(actions: any): Generator<any, void, any> {
  console.log("holdSale1");
  let payload = actions.payload;
  try {
    let saleData = payload.dataToSend;
    saleData = deleteErrorsField(saleData, saleData.saleStatus === "Held" ? true : false);
    let response = yield commonPostData(ENDPOINT.POS.saveSaleData(), saleData);
    updateAdhocTables(response);

    if (response?.errors?.includes(INSUFFICIENT_STOCK) || response?.warnings?.includes(ITEM_SOLD_OUT)) {
      yield put(saveBackOrderShowModal(true));
    }

    if (response && !!Object.keys(response)?.length && !response?.errors?.length && !response?.warnings?.length && !response?.code && !hasItemLeveErrors(response?.saleItems)) {
      if (!response.operation) updateSyncedSale(response);

      payload.callback && payload.callback(response);
      !actions.payload?.doNotResetData && resetSaleData();
      actions.payload?.getChannelModal && actions.payload?.getChannelModal();
    }
    else if (response?.code) {
      if (response.code === "REST_RECORD_ALREADY_EXISTS") {
        notifyError(getErrorMessage(response.code));
      }
      if (response.code === "REST_ERR_INVALID_INPUT") {
        notifyError(getErrorMessage(response.code));
      }
    }
    else {
      calculateAndSaveSaleData(response);
      if (!!response?.errors?.length && !response?.errors?.includes(INSUFFICIENT_STOCK)) {
        yield put((orderLevelError({ isToastOpen: true, toastMessage: response.errors, toastType: "error", duration: null })));
      }
      if (!!response?.warnings?.length && !response?.warnings?.includes(ITEM_SOLD_OUT)) {
        response.warnings.forEach((error: any) => {
          notifyError(pos_errormap[error]);
        });
      }
    }
  } catch (error) {
    console.log(error);
  }
}

/**
 * Reopen a closed sale. **WEB-ONLY — mobile has no counterpart and no watcher for it.**
 */
function* reopenSale(action: any): Generator<any, void, any> {
  try {
    const response = yield api.request({
      method: METHODS.POST,
      url: ENDPOINT.SALE.saleReopen(action.payload.saleId),
    });
    if (response && !response?.code && !response.operation) {
      updateSyncedSale(response);
    }
    if (action.payload?.callback) {
      action.payload.callback(response);
    }
  } catch (error) {
    if (action.payload?.errorCallback) {
      action.payload.errorCallback(error);
    }
  }
}

export default function* root(): Generator<any, void, any> {
  yield takeEvery(holdSaleData.type, holdSale);
  yield takeEvery(reopenOrder.type, reopenSale);
}
